import {
  Check,
  ArrowDownToLine,
  Ban,
  AlertTriangle,
  Diamond,
  CloudOff,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ParityState } from '@/services/api/channelSync.api';

// ---------------------------------------------------------------------------
// Single source of truth for the D6 parity states — color AND icon/glyph AND
// text (never color-only, §5 a11y). Reused verbatim on every surface: the Sync
// panel grid, the Rate-Calendar overlay, the /rate-parity report and the
// SUPER_ADMIN rollup. `synced`/`unknown` are the quiet states — they carry no
// badge on the dense calendar (only non-synced states badge it, Q4).
// ---------------------------------------------------------------------------

/** The six badge-able states (excludes the quiet `synced` / `unknown`). */
export type BadgeableParityState = Exclude<ParityState, 'synced' | 'unknown'>;

interface ParityMeta {
  label: string;
  glyph: string;
  icon: LucideIcon;
  badge: string; // pill bg + text
  dot: string; // dot bg (calendar/grid marker)
  swatch: string; // legend swatch
}

export const PARITY_META: Record<BadgeableParityState, ParityMeta> = {
  external_change: {
    label: 'Changed on AxisRooms',
    glyph: '↓',
    icon: ArrowDownToLine,
    badge: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-500',
    swatch: 'bg-blue-500',
  },
  closed: {
    label: 'Closed',
    glyph: '▨',
    icon: Ban,
    badge: 'bg-slate-200 text-slate-700',
    dot: 'bg-slate-500',
    swatch: 'bg-slate-500',
  },
  push_unconfirmed: {
    label: 'Not confirmed',
    glyph: '▲',
    icon: AlertTriangle,
    badge: 'bg-amber-100 text-amber-800',
    dot: 'bg-amber-500',
    swatch: 'bg-amber-500',
  },
  ota_divergence: {
    label: 'OTAs differ',
    glyph: '◆',
    icon: Diamond,
    badge: 'bg-purple-100 text-purple-700',
    dot: 'bg-purple-500',
    swatch: 'bg-purple-500',
  },
  sync_failed: {
    label: "Couldn't verify",
    glyph: '⚠',
    icon: CloudOff,
    badge: 'bg-gray-100 text-gray-500',
    dot: 'bg-gray-400',
    swatch: 'bg-gray-400',
  },
};

const SYNCED_META: ParityMeta = {
  label: 'Synced',
  glyph: '●',
  icon: Check,
  badge: 'bg-green-100 text-green-700',
  dot: 'bg-green-500',
  swatch: 'bg-green-500',
};

/** Only non-synced states earn a visible badge on the dense calendar. */
export function isBadgeableState(state: string): state is BadgeableParityState {
  return state in PARITY_META;
}

export function parityStateLabel(state: ParityState): string {
  if (state === 'synced') return SYNCED_META.label;
  if (state === 'unknown') return 'Not synced';
  return PARITY_META[state].label;
}

/**
 * Parity badge / dot atom. `size="dot"` renders the decorative marker used on
 * the calendar (aria-hidden — the state reaches the reader via the cell's
 * aria-label); `sm`/`md` render the full icon + text pill.
 */
export function ParityBadge({
  state,
  size = 'md',
  className,
}: {
  state: ParityState;
  size?: 'md' | 'sm' | 'dot';
  className?: string;
}) {
  const meta = state === 'synced' ? SYNCED_META : isBadgeableState(state) ? PARITY_META[state] : null;
  if (!meta) return null;

  if (size === 'dot') {
    return (
      <span
        className={cn('inline-block w-2 h-2 rounded-full', meta.dot, className)}
        aria-hidden="true"
      />
    );
  }

  const Icon = meta.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap',
        meta.badge,
        size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs',
        className,
      )}
    >
      <Icon size={size === 'sm' ? 10 : 12} aria-hidden="true" />
      {meta.label}
    </span>
  );
}
